import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, ScopeBadge } from "@/components/common";
import { useDebounced } from "@/components/DataTable";
import { AnnexUpdate } from "@/components/AnnexUpdate";
import { AnnexCheckReport, AnnexCheckedAgainst } from "@/components/AnnexCheckReport";

const COMMODITIES = ["Cattle", "Cocoa", "Coffee", "Oil palm", "Rubber", "Soya", "Wood"];
const EMPTY = { hs_code: "", commodity: "Wood", scope: "in_scope", description: "", legal_basis: "", notes: "", condition: "", applies_from: "2026-12-30", source: "", active: true };

function AnnexDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(row?.id ? { ...EMPTY, ...row, applies_from: (row.applies_from || "2026-12-30").slice(0, 10) } : EMPTY); }, [row]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { hs_code: f.hs_code, commodity: f.commodity, scope: f.scope, description: f.description, legal_basis: f.legal_basis, notes: f.notes,
        condition: f.condition, applies_from: f.applies_from || null, source: f.source, active: true };
      const r = row?.id ? await api.put(`/annex/${row.id}`, body) : await api.post("/annex", body);
      toast.success(`Saved ${r.data.row.hs_code}. ${fmtNum(r.data.materials_changed)} material record(s) re-evaluated.`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="annex-dialog">
        <DialogHeader><DialogTitle>{row?.id ? `Edit HS ${row.hs_code}` : "Add Annex I code"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">HS / CN prefix</label>
            <Input data-testid="annex-hs-input" className="font-mono" value={f.hs_code} onChange={set("hs_code")} placeholder="e.g. 4819 or 940360" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Commodity</label>
            <Select value={f.commodity} onValueChange={set("commodity")}>
              <SelectTrigger data-testid="annex-commodity-select"><SelectValue /></SelectTrigger>
              <SelectContent>{COMMODITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Classification</label>
            <Select value={f.scope} onValueChange={set("scope")}>
              <SelectTrigger data-testid="annex-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_scope">In scope</SelectItem>
                <SelectItem value="check">Check (partially in scope)</SelectItem>
                <SelectItem value="out_of_scope">Out of scope (explicit exclusion)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <Input data-testid="annex-description-input" value={f.description} onChange={set("description")} />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Legal basis</label>
            <Input value={f.legal_basis} onChange={set("legal_basis")} placeholder="Reg (EU) 2023/1115 Annex I…" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Condition</label>
            <Input data-testid="annex-condition-input" value={f.condition} onChange={set("condition")} placeholder="e.g. Only if of wood" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Applies from</label>
            <Input type="date" data-testid="annex-applies-from-input" value={f.applies_from} onChange={set("applies_from")} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Source</label>
            <Input data-testid="annex-source-input" value={f.source} onChange={set("source")} placeholder="Annex I as amended by Delegated Reg (EU) 2026/2102" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</label>
            <Textarea rows={2} value={f.notes} onChange={set("notes")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="annex-save-btn" onClick={save} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save & re-evaluate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MatchTester() {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const dc = useDebounced(code, 350);
  useEffect(() => {
    if (!dc) { setRes(null); return; }
    api.get("/annex/match", { params: { code: dc } }).then((r) => setRes(r.data));
  }, [dc]);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3">
      <FlaskConical className="h-4 w-4 text-slate-400" />
      <span className="text-sm font-medium text-slate-700">Test a commodity code</span>
      <Input data-testid="annex-test-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="9403600000" className="h-8 w-44 font-mono" />
      {res && (
        <span className="flex items-center gap-2 text-sm text-slate-600" data-testid="annex-test-result">
          <ScopeBadge scope={res.scope_auto} /> {res.annex_code ? <>via <span className="font-mono">{res.annex_code}</span> {res.commodity && `· ${res.commodity}`}</> : "no Annex I match"}
        </span>
      )}
    </div>
  );
}

export default function AnnexI() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [commodity, setCommodity] = useState("all");
  const [edit, setEdit] = useState(null);
  const dq = useDebounced(q);
  const load = useCallback(() => {
    api.get("/annex", { params: { q: dq || undefined, commodity: commodity === "all" ? undefined : commodity } }).then((r) => setRows(r.data));
  }, [dq, commodity]);
  useEffect(() => { load(); }, [load]);
  const del = async (r) => {
    if (!window.confirm(`Delete HS ${r.hs_code}? Materials matched to it will be re-evaluated.`)) return;
    try {
      const res = await api.delete(`/annex/${r.id}`);
      toast.success(`Deleted. ${fmtNum(res.data.materials_changed)} material record(s) re-evaluated.`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const groups = COMMODITIES.map((c) => ({ c, rows: rows.filter((r) => r.commodity === c) })).filter((g) => g.rows.length);
  return (
    <div data-testid="annex-page">
      <PageHeader title="Annex I reference" subtitle="EUDR Annex I HS codes (Reg (EU) 2023/1115 as amended by Reg (EU) 2025/2650 — printed products of ch. 49 removed — and Del. Reg (EU) 2026/2102). Materials match on the longest prefix; unmatched codes are Out of scope.">
        {can.override && <AnnexCheckReport />}
        {can.override && <Button data-testid="annex-add-btn" onClick={() => setEdit({})}><Plus className="mr-1.5 h-4 w-4" /> Add code</Button>}
      </PageHeader>
      <AnnexCheckedAgainst key={rows.length} />
      {can.override && <AnnexUpdate onApplied={load} />}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="annex-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or description" className="h-9 bg-white pl-9" />
        </div>
        <Select value={commodity} onValueChange={setCommodity}>
          <SelectTrigger className="h-9 w-44 bg-white" data-testid="annex-commodity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All commodities</SelectItem>
            {COMMODITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto"><MatchTester /></div>
      </div>
      <div className="space-y-5">
        {groups.map((g) => (
          <Panel key={g.c} title={g.c} subtitle={`${g.rows.length} codes`} testid={`annex-group-${g.c}`}>
            <div className="-m-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-2 text-left">HS prefix</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-left">Classification</th>
                    <th className="px-3 py-2 text-left">Condition</th><th className="px-3 py-2 text-left">Applies from</th>
                    <th className="px-3 py-2 text-left">Source / notes</th><th className="px-3 py-2 text-right">Materials</th><th className="px-3 py-2 text-left">Updated</th><th className="px-5 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {g.rows.map((r) => (
                    <tr key={r.id} data-testid={`annex-row-${r.hs_code}`} className="hover:bg-slate-50/60">
                      <td className="px-5 py-2.5 font-mono font-semibold text-slate-900">{r.hs_code}</td>
                      <td className="max-w-md px-3 py-2.5 text-slate-700">{r.description}</td>
                      <td className="px-3 py-2.5"><ScopeBadge scope={r.scope} /></td>
                      <td className="max-w-[200px] px-3 py-2.5 text-xs text-slate-700" data-testid={`annex-condition-${r.hs_code}`}>{r.condition || "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs" data-testid={`annex-applies-from-${r.hs_code}`}>{fmtDate(r.applies_from)}</td>
                      <td className="max-w-xs px-3 py-2.5 text-xs text-slate-500" data-testid={`annex-source-${r.hs_code}`}>{r.source || r.legal_basis}{r.notes && <span className="block text-slate-600">{r.notes}</span>}</td>
                      <td className="px-3 py-2.5 text-right font-medium">{fmtNum(r.materials)}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{fmtDate(r.updated_at)}<span className="block">{r.updated_by}</span></td>
                      <td className="whitespace-nowrap px-5 py-2.5 text-right">
                        {can.override && (
                          <>
                            <button data-testid={`annex-edit-${r.hs_code}`} onClick={() => setEdit(r)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                            <button data-testid={`annex-delete-${r.hs_code}`} onClick={() => del(r)} className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        ))}
      </div>
      <AnnexDialog row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
